import axios, { ResponseType } from "axios";
import { AppState } from "store";
import { displayErrorMessage } from "store/actions/utilActions";
import WintApiClient, { httpGet, httpPost, httpPut, httpDelete } from "wint-api-client";
import { AuthEnums, LocaleEnums } from "models/enums";
import { camelizeKeys } from "humps";
import { TranslationKeys } from "translations";
import { setAuthTokens } from "store/actions/authActions";
import { sessionExpired } from "store/actions/onboardingActions";
import { Person } from "models/stateTypes/authState";
import { ResponseError } from "models/utilTypes/apiClientTypes";
import { InvoiceConstants } from "utils/constants";
import { AuthHelper, LocalizationHelperInstance, SessionHelper } from "utils/helpers";
import { getRefreshTokenPromise, setRefreshTokenPromise } from "utils/helpers/refreshTokenLock";
import { decodeErrorBody } from "utils/helpers/errorBody";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { ServiceConstants } from "utils/constants";
import OffboardingMaterialStorage from "utils/helpers/offboardingMaterialStorage";
import {
  GENERIC_ONBOARDING_SCOPE,
  ONBOARDING_SESSION_KEY_PREFIX,
  getOnboardingSessionStorageKey,
} from "utils/helpers/onboardingPerson";
import { readCompanySession, readPersonSession } from "utils/helpers/onboardingSessionStorage";

export { ONBOARDING_SESSION_KEY_PREFIX, getOnboardingSessionStorageKey };

function useAPIClientHelper() {
  const currentLang = useSelector((store: AppState) => store.common.locale);
  const selectedCompanyId = useSelector((store: AppState) => store.auth.selectedCompany?.id);
  // Read from the route directly rather than only from Redux: the Redux copy is mirrored in by an
  // effect, and an effect further down the tree can run first -- so on a reload straight into an
  // already-valid session, a request could fire before that mirror ever lands, sending no session
  // header at all. useParams is synchronous on the very first render, so it can't race like that.
  const { inviteToken: routeInviteToken } = useParams<{ inviteToken: string }>();
  const storedInviteToken = useSelector((store: AppState) => store.onboarding.inviteToken);
  const inviteToken = routeInviteToken ?? storedInviteToken;

  const dispatch = useDispatch();

  const createClient = (
    baseUrl: string,
    authType: AuthEnums.AuthType = AuthEnums.AuthType.Company,
    responseType: ResponseType = "json",
    contentType: string = "application/json",
    showErrors: boolean = false,
    skipUndefinedConversion: boolean = false,
    timeout?: number,
    hideErrorsForCodes?: string[],
    pinnedOnboardingToken?: string,
    companyId?: number
  ) => {
    // The onboarding token this client's request actually carried, so a late refusal can be judged
    // against the session it was sent under rather than whichever one is current by then.
    let sentOnboardingToken: string | undefined;
    const setInterceptorsOnRequest = () => {
      axiosInstance.interceptors.request.use(
        (config) => {
          const headers = getHeaders(authType, contentType);
          config.baseURL = baseUrl;
          Object.entries(headers).forEach(([key, value]) => {
            // A FormData body needs axios's own multipart boundary in Content-Type, which
            // setting the header here would overwrite before axios ever sees the request.
            if (key === "Content-Type" && config.data instanceof FormData) return;
            if (value !== undefined) config.headers.set(key, value);
          });
          if (timeout !== undefined) config.timeout = timeout;
          config.responseType = responseType;
          return config;
        },
        (error) => onError(error, showErrors, hideErrorsForCodes)
      );
    };

    const setInterceptorsOnResponse = () => {
      axiosInstance.interceptors.response.use(
        (response) => {
          if (response.data && responseType === "json") {
            response.data = camelizeKeys(
              skipUndefinedConversion
                ? response.data
                : JSON.parse(JSON.stringify(response.data, (key, value) => (value === null ? undefined : value))),
              function (key, convert) {
                return /[_]/.test(key) ? key.charAt(0).toLowerCase() + key.slice(1) : convert(key);
              }
            );
          }
          return response;
        },
        (error) => onError(error, showErrors, hideErrorsForCodes)
      );
    };

    const getHeaders = (authType: AuthEnums.AuthType, contentType: string) => {
      const defaultHeader = {
        "Content-Type": contentType,
        "Accept-Language": currentLang.language === "sv" ? "sv-SE" : "en-US",
        "Wint-Client": import.meta.env.VITE_APP_NAME,
        "Wint-Client-Version": import.meta.env.VITE_VERSION,
      };

      switch (authType) {
        case AuthEnums.AuthType.User:
          return { ...defaultHeader, Authorization: `Bearer ${getAccessToken()}` };
        case AuthEnums.AuthType.Company:
          return {
            ...defaultHeader,
            Authorization: `Bearer ${getAccessToken()}`,
            CompanyId: (companyId ?? selectedCompanyId)?.toString(),
            [ServiceConstants.CLIENT_CAPABILITIES_HEADER]: ServiceConstants.CLIENT_CAPABILITIES,
          };
        case AuthEnums.AuthType.Onboarding: {
          // Pinned when the request was queued under a particular session: reading the shared slot
          // at send time would use a company the visitor has since switched to.
          const onboardingToken = pinnedOnboardingToken ?? getOnboardingSessionToken();
          sentOnboardingToken = onboardingToken;
          return {
            ...defaultHeader,
            [ServiceConstants.ONBOARDING_SESSION_HEADER]: onboardingToken ? `Bearer ${onboardingToken}` : undefined,
          };
        }
        case AuthEnums.AuthType.OnboardingPerson: {
          const personToken = getOnboardingPersonSessionToken();
          return {
            ...defaultHeader,
            [ServiceConstants.ONBOARDING_SESSION_HEADER]: personToken ? `Bearer ${personToken}` : undefined,
          };
        }
        case AuthEnums.AuthType.OnboardingInvite:
          // Anonymous apart from the invite. Core requires the header on the begin
          // call and on every status poll, so it is set here rather than per call.
          return {
            ...defaultHeader,
            [ServiceConstants.INVITE_TOKEN_HEADER]: inviteToken ?? undefined,
          };
        case AuthEnums.AuthType.OffboardingLink:
          return {
            ...defaultHeader,
            [ServiceConstants.OFFBOARDING_LINK_HEADER]: OffboardingMaterialStorage.getLinkToken(),
          };
        case AuthEnums.AuthType.Offboarding: {
          const offboardingSession = OffboardingMaterialStorage.getSession();
          return {
            ...defaultHeader,
            [ServiceConstants.OFFBOARDING_SESSION_HEADER]: offboardingSession
              ? `Bearer ${offboardingSession.sessionToken}`
              : undefined,
          };
        }
        case AuthEnums.AuthType.Unauthorized:
        default:
          return defaultHeader;
      }
    };

    const getAccessToken = () => {
      const session = SessionHelper.getSessionItem<Person>("user_session");
      const authTokens = session?.authTokens;

      if (authTokens && authTokens.accessToken) {
        return authTokens.accessToken;
      } else {
        //Session logout when migrate from basic auth to bearer auth
        AuthHelper.logout();
      }
    };

    // AuthType.Onboarding is only used inside the onboarding wizard, so the absence of an invite
    // in the route means the generic flow rather than "not onboarding".
    const onboardingScope = inviteToken ?? GENERIC_ONBOARDING_SCOPE;

    const getOnboardingSessionToken = () => {
      const session = readCompanySession(onboardingScope);
      // Belt and braces: the slot is keyed by scope, so a mismatch should not happen, but
      // sending a token for the wrong invite is exactly the failure this must never risk.
      return session?.inviteToken === onboardingScope ? session.token : undefined;
    };

    const getOnboardingPersonSessionToken = () => readPersonSession()?.token;

    const onError = (error: any, showErrors?: boolean, hideErrorsForCodes?: string[]) => {
      // Handle cancelled requests silently
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.__CANCEL__) {
        return Promise.reject({ status: "cancelled", message: "Request cancelled" });
      }

      const data = decodeErrorBody(error?.response?.data);

      if (error?.response && error.response.status === 401 && !error.config?.url?.endsWith("/refresh")) {
        // With an explicit company a 401 means "no access to that company" unless Core flags the token as
        // expired, so only then does it go through the refresh-or-logout path.
        if (companyId === undefined || error.response.headers?.["token-expired"] === "true") {
          return refreshExpiredAccessToken(error);
        }
      } else {
        showErrors &&
          dispatch(
            displayErrorMessage(
              // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
              error?.response?.status == 403
                ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.NotEnoughPermissions)
                : data
                  ? getErrorDescriptions(data, hideErrorsForCodes)
                  : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.SomethingWentWrong),
              { showSupportLink: needsSupportCase(data, hideErrorsForCodes) }
            )
          );
      }

      return Promise.reject({
        status: error?.response?.status,
        codes: getErrorCodes(data),
        message: getErrorDescriptions(data),
        errors: getErrors(data),
      });
    };

    /**
     * Only Wint can activate a booking account, so these errors are shown with the support-case link
     * rather than leaving the customer to find their own way to us. Reads the same filtered set
     * getErrorDescriptions shows, so a hidden code cannot attach the link to another error.
     */
    const needsSupportCase = (errors: any, hideErrorsForCodes?: string[]) =>
      getErrorCodes(errors)
        .filter((code) => !hideErrorsForCodes?.includes(code))
        .some((code) => InvoiceConstants.InactiveAccountErrorCodes.includes(code));

    // Onboarding puts the code in `title` and `detail` alike and the human copy only here, so
    // without reading this the toast shows the customer "Form.AlreadySubmitted" verbatim.
    const localizedText = (localizedMessage: unknown): string | undefined => {
      if (!localizedMessage || typeof localizedMessage !== "object") return undefined;
      const byLocale = localizedMessage as Record<string, string>;
      return byLocale[currentLang.language] ?? byLocale[LocaleEnums.LocaleType.sv];
    };

    // RFC 7807 bodies from the onboarding surfaces, which do not use Wint's own
    // { code, description } list. Core's onboarding controller and Onboarding's
    // OnboardingProblem both put the code in `title` today. `code` is read first
    // anyway, so a shared envelope that later carries a real code field needs no
    // change here.
    const asProblemDetails = (errors: any): { code?: string; message?: string } | undefined => {
      if (!errors || Array.isArray(errors) || typeof errors !== "object") return undefined;
      const { code, title, detail, status, localizedMessage } = errors as Record<string, unknown>;
      const looksLikeProblem = typeof status === "number" && (typeof title === "string" || typeof detail === "string");
      if (!looksLikeProblem) return undefined;
      return {
        code: (code as string) ?? (title as string) ?? undefined,
        message: localizedText(localizedMessage) ?? (detail as string) ?? (title as string) ?? undefined,
      };
    };

    const getErrors = (errors: any): ResponseError[] => {
      const errorObj = errors ? camelizeKeys(errors) : undefined;
      return Array.isArray(errorObj) ? (errorObj as ResponseError[]) : [];
    };

    const getErrorCodes = (errors: any) => {
      if (errors) {
        const errorObj = camelizeKeys(errors) as ResponseError[];
        const problem = asProblemDetails(errorObj);
        if (problem) return problem.code ? [problem.code] : [];
        if (Array.isArray(errorObj)) return errorObj.map((e) => e.code);
        const code = (errorObj as { code?: unknown } | null)?.code;
        return typeof code === "string" ? [code] : [];
      }
      return [];
    };

    // The support-case link is appended after the whole joined text, so these go last for the link
    // to follow the sentence it belongs to. The sort is stable: other errors keep their order.
    const inactiveAccountErrorsLast = (errorList: ResponseError[]) =>
      [...errorList].sort(
        (a, b) =>
          Number(InvoiceConstants.InactiveAccountErrorCodes.includes(a.code)) -
          Number(InvoiceConstants.InactiveAccountErrorCodes.includes(b.code))
      );

    const getErrorDescriptions = (errors: any, hideErrorsForCodes?: string[]) => {
      if (errors) {
        const errorObj = camelizeKeys(errors) as ResponseError[];
        const problem = asProblemDetails(errorObj);
        if (problem) {
          const hidden = problem.code ? hideErrorsForCodes?.includes(problem.code) : false;
          return hidden ? "" : (problem.message ?? "");
        }
        return Array.isArray(errorObj)
          ? inactiveAccountErrorsLast(
              hideErrorsForCodes
                ? errorObj.filter((e) => hideErrorsForCodes.findIndex((hiddenCode) => hiddenCode === e.code) === -1)
                : errorObj
            )
              .map((e) => e.description)
              .join(", ")
              .replaceAll(":,", ":")
              .replaceAll(".,", ".")
          : typeof errorObj === "object" && errorObj !== null && "message" in errorObj
            ? (errorObj as any).message
            : "";
      }
      return "";
    };

    const refreshExpiredAccessToken = (error: any) => {
      if (authType === AuthEnums.AuthType.Onboarding) {
        // Clears storage *and* the store. Storage alone leaves the wizard on screen with a
        // session it no longer has: every call 401s and the visitor sees a generic error
        // instead of being sent back to identification, which is the only recovery there is.
        //
        // Only when the refused token is still the current one: a late refusal of a session that
        // has since been replaced says nothing about the replacement, and clearing it would end a
        // good session and spend the re-mint budget.
        const current = readCompanySession(onboardingScope)?.token;
        if (!sentOnboardingToken || sentOnboardingToken === current) {
          dispatch(sessionExpired(onboardingScope));
        }
        return Promise.reject(error);
      }

      // Not the generic logout below: a refused person session must not log out a Wint user who may
      // be signed in in another tab.
      if (authType === AuthEnums.AuthType.OnboardingPerson) {
        return Promise.reject(error);
      }

      // Nothing to clear: the invite token is mirrored from the route, not a session.
      // Falling through matters though — the generic path ends in AuthHelper.logout(),
      // whose LOGOUT resets the onboarding slice, so a refused invite would log out a
      // visitor who never logged in and take the token with it. Reject and let the
      // caller render the refusal.
      if (authType === AuthEnums.AuthType.OnboardingInvite) {
        return Promise.reject(error);
      }

      // The offboarding material has no Wint login to refresh or log out of. A refused session sends the
      // customer back to BankID, which the page does once the session is gone.
      if (authType === AuthEnums.AuthType.Offboarding) {
        OffboardingMaterialStorage.clearSession();
        return Promise.reject(error);
      }
      if (authType === AuthEnums.AuthType.OffboardingLink) {
        return Promise.reject(error);
      }

      const originalRequest = error.config;
      const session = SessionHelper.getSessionItem<Person>("user_session");
      const refreshToken = session?.authTokens?.refreshToken;

      if (refreshToken && !originalRequest._retry) {
        originalRequest._retry = true;

        if (!getRefreshTokenPromise()) {
          const promise = apiClientInstance(AuthEnums.AuthType.Unauthorized)
            .AuthClient.RefreshToken(refreshToken)
            .then((res) => {
              const currentSession = SessionHelper.getSessionItem<Person>("user_session");
              if (!currentSession) {
                return;
              }
              if (res.data.authTokens) {
                SessionHelper.saveToSession("user_session", { ...currentSession, authTokens: res.data.authTokens });
                dispatch(setAuthTokens(res.data.authTokens));
              }
            })
            .catch(() => {
              AuthHelper.logout();
              throw error;
            })
            .finally(() => {
              setRefreshTokenPromise(null);
            });
          setRefreshTokenPromise(promise);
        }

        return getRefreshTokenPromise()!.then(() => {
          setInterceptorsOnRequest();
          return axiosInstance.request(originalRequest);
        });
      } else {
        AuthHelper.logout();
        return Promise.reject(error);
      }
    };

    const axiosInstance = axios.create();
    setInterceptorsOnRequest();
    setInterceptorsOnResponse();
    return axiosInstance;
  };

  const apiClientInstance = (
    type?: AuthEnums.AuthType,
    params?: {
      showErrors?: boolean;
      hideErrorsForCodes?: string[];
      responseType?: ResponseType;
      contentType?: string;
      skipUndefinedConversion?: boolean;
      timeout?: number;
      /** Sends this onboarding session token instead of the one in storage at send time. */
      onboardingSessionToken?: string;
      /** Reads a company-scoped endpoint for this company instead of the selected one. */
      companyId?: number;
    }
  ) => {
    let apiUrl = import.meta.env.VITE_SUPERKOLL_SERVER_URL ?? "/";
    if (localStorage.getItem("API_ENVIRONMENT")) {
      apiUrl = localStorage.getItem("API_ENVIRONMENT") as string;
    }

    // Check if the current domain is appstaging.wint.se to route to apistaging.wint.se
    if (window.location.hostname === "appstaging.wint.se") {
      apiUrl = "https://apistaging.wint.se";
    }

    // When running locally with SWA (localhost:4280), keep using "/"
    // The proxy Azure Function will handle backend API calls to avoid CORS issues
    // /api/content is handled by the content function, other /api/* go through proxy function

    const instance = createClient(
      apiUrl,
      type,
      params?.responseType,
      params?.contentType,
      params?.showErrors,
      params?.skipUndefinedConversion,
      params?.timeout,
      params?.hideErrorsForCodes,
      params?.onboardingSessionToken,
      params?.companyId
    );
    const client = new WintApiClient(instance);
    return Object.assign(client, {
      get: httpGet,
      post: httpPost,
      put: httpPut,
      delete: httpDelete,
      // httpPost always JSON.stringifies its body, which would turn a FormData payload into
      // "{}". Posted straight through the same axios instance instead, so the shared auth
      // headers, error handling and token refresh still apply.
      postForm: (url: string, formData: FormData) => instance.post(url, formData),
      // No httpPatch exists in the generated client. Same reasoning as postForm.
      patchJson: (url: string, data: unknown) => instance.patch(url, JSON.stringify(data)),
    });
  };

  return {
    apiClientInstance,
  };
}

export default useAPIClientHelper;
